/**
 * Session restore helpers — refresh via HttpOnly cookie with retries on transient failures.
 */

import type { LoginResponse, ApiResponse  } from '@lambda/shared/types';

const RESTORE_RETRY_ATTEMPTS = 3;
const RESTORE_RETRY_DELAY_MS = 400;

export class SessionRefreshUnauthorizedError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'SessionRefreshUnauthorizedError';
    this.code = code;
  }
}

export class SessionRefreshTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionRefreshTransientError';
  }
}

/** Read legacy access token from localStorage (pre–HttpOnly-cookie sessions). */
export function readLegacyAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('bhg-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string };
    return typeof parsed?.token === 'string' && parsed.token.length > 0 ? parsed.token : null;
  } catch {
    return null;
  }
}

export function getAccessTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isAccessTokenStillValid(token: string, bufferMs = 30_000): boolean {
  const expMs = getAccessTokenExpiryMs(token);
  if (!expMs) return false;
  return expMs - Date.now() > bufferMs;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isTransientRefreshFailure(err: unknown): boolean {
  if (err instanceof SessionRefreshUnauthorizedError) return false;
  if (err instanceof SessionRefreshTransientError) return true;
  if (err instanceof TypeError) return true; // network / CORS fetch failure
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('failed to fetch') || msg.includes('network')) return true;
    if (/\b5\d{2}\b/.test(msg)) return true;
  }
  return true;
}

/**
 * Retry refresh on cold-start / gateway blips; do not retry definitive 401s.
 */
export async function restoreSessionWithRetry(
  refresh: () => Promise<ApiResponse<LoginResponse>>
): Promise<LoginResponse> {
  let lastError: unknown;

  for (let attempt = 0; attempt < RESTORE_RETRY_ATTEMPTS; attempt += 1) {
    try {
      const res = await refresh();
      if (res.success && res.data) {
        return res.data;
      }
      const code = res.error?.code;
      const message = res.error?.message ?? 'Session refresh failed.';
      throw new SessionRefreshUnauthorizedError(message, code);
    } catch (err) {
      lastError = err;
      if (!isTransientRefreshFailure(err) || attempt >= RESTORE_RETRY_ATTEMPTS - 1) {
        throw err;
      }
      await delay(RESTORE_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError instanceof Error ? lastError : new SessionRefreshTransientError('Session refresh failed.');
}
